import { DatePipe, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, PrimeTemplate } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { ToastService } from 'src/app/core/services/toast.service';
import { NewFreelancerRequestService } from 'src/app/main/home-freelancers/new-freelancer-request/new-freelancer-request.service';
import { FilesRequest } from 'src/app/models/FilesRequest';
import { QrCodeDialogComponent } from 'src/app/shared/components/qr-code-dialog/qr-code-dialog.component';
import { STATES_TO_HIDE_CANCEL_OTP_BUTTON, STATES_TO_HIDE_CONTINUE_OTP_BUTTON, SUBSTATES_TO_REOPEN_BELENDER } from 'src/app/shared/constants/common.constants';
import { UtilsService } from 'src/app/utils/utils.service';
import { IrpfTableService } from '../../../shared/components/irpf-table/irpf-table.service';
import { LoadingComponent } from '../../../utils/loading/loading.component';
import { NewFreelancerRequestComponent } from '../new-freelancer-request/new-freelancer-request.component';
import { RequestDetailDocumentsComponent } from './freelancer-request-detail-documents/freelancer-request-detail-documents.component';
import { FreelancerRequestDetailFeasibilityComponent } from './freelancer-request-detail-feasibility/freelancer-request-detail-feasibility.component';
import { FreelancerRequestDetailFeasibilityService } from './freelancer-request-detail-feasibility/freelancer-request-detail-feasibility.service';
import { FreelancerRequestDetailHeaderComponent } from './freelancer-request-detail-header/request-detail-header.component';
import { FreelancerRequestDetailService } from './freelancer-request-detail.service';

@Component({
    selector: 'app-freelancer-request-detail',
    templateUrl: './freelancer-request-detail.component.html',
    standalone: true,
    imports: [
        NgIf,
        DrawerModule,
        PrimeTemplate,
        FreelancerRequestDetailFeasibilityComponent,
        LoadingComponent,
        NewFreelancerRequestComponent,
        FreelancerRequestDetailHeaderComponent,
        RequestDetailDocumentsComponent,
        RouterOutlet,
        DatePipe,
        TranslateModule,
        TooltipModule,
        QrCodeDialogComponent
    ]
})
export class FreelancerRequestDetailComponent implements OnInit, OnDestroy {

    items = [];
    newRequestStatus: {
		title: string, step: number, requestLastStatus?: string
	} = { title: 'New request', step: 1 };

    belenderStatus: {
		actualStatus: string;
		actualStatusMessage: string;
	} = { actualStatus: '', actualStatusMessage: '' };
    consentBelenderUrl: SafeResourceUrl;
    isDownloadZipButtonDisabled = false;

    constructor(
		readonly freelancerRequestDetailService: FreelancerRequestDetailService,
        private readonly toastService: ToastService,
		private readonly sanitizer: DomSanitizer,
		private readonly translate: TranslateService,
		private readonly confirmationService: ConfirmationService,
		private readonly freelancerRequestDetailFeasibilityService: FreelancerRequestDetailFeasibilityService,
        readonly newFreelancerRequestService: NewFreelancerRequestService,
        readonly utilsService: UtilsService,
        private readonly irpfTableService: IrpfTableService
    ) { }

    async ngOnInit(): Promise<void> {
        await this.freelancerRequestDetailService.getRequestFromUrl();
    }

    ngOnDestroy(): void {
        // reset the feasibility when the user navigates away from the request detail page
        this.freelancerRequestDetailFeasibilityService.resetFeasibility();
        this.freelancerRequestDetailService.resetFeasibilityData();
    }

    reopenOTPWidget(): void {
        try {
            this.freelancerRequestDetailService.isLoading = true;
            this.newRequestStatus = {
                title: "Continuar con el OTP",
                step: 3,
                requestLastStatus: this.freelancerRequestDetailService.request.personQuery.substatus
            };
            this.belenderStatus = {
                actualStatus: this.freelancerRequestDetailService.request.personQuery.substatus,
                actualStatusMessage: this.freelancerRequestDetailService.request.personQuery.substatus
            };

            this.consentBelenderUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.freelancerRequestDetailService.request.personQuery.consentUrlBoxed);
            this.newFreelancerRequestService.isFinishingOTP = true;
        } catch (e) {
            console.log(e);
            this.toastService.showError(`Error ${e.status} (${e.statusText})`, `${e.error.detail}`, false);
        } finally {
            this.freelancerRequestDetailService.isLoading = false;
        }
    }

    async cancelOTP(): Promise<void> {
        try {
            this.freelancerRequestDetailService.isLoading = true;

            const cancelResponse = await this.freelancerRequestDetailService.cancelRequest({
                queryId: this.freelancerRequestDetailService.queryId,
                requestReference: undefined,
                userName: undefined,
                userPassword: undefined
            });

            if (cancelResponse.status !== 'Canceled')
                this.toastService.showError('TOAST.ERROR', `${cancelResponse.status}`, false, 10000);
            else {
                this.toastService.showSuccess('TOAST.SUCCESS', 'NEW_REQUEST.CANCEL', true, 10000);
                await this.freelancerRequestDetailService.getRequestFromUrl(true);
            }
        } catch (e) {
            this.toastService.showError(`Error ${e.error.status} (${e.error.title})`, `${e.error.detail}`, false);
        } finally {
            this.freelancerRequestDetailService.isLoading = false;
        }
    }

    confirmCancelOTP(event: Event): void {
        if (!event || !event.target) return;

        this.confirmationService.confirm({
            target: event.target,
            message: this.translate.instant('REQUEST_DETAIL.CANCEL_REQUEST_CONFIRMATION'),
            acceptLabel: this.translate.instant('BUTTON.YES'),
            rejectLabel: this.translate.instant('BUTTON.NO'),
            accept: () => {
                this.cancelOTP().catch(error => { console.error(error); });
            }
        });
    }

    verifyRequestCanBeReopened(): boolean {
        const request = this.freelancerRequestDetailService.request;
        if (!request) return false;

        const status = request.personQuery.status;
        if (status.toLowerCase() !== 'processing') return false;

        const substatus = request.personQuery.substatus;
        return SUBSTATES_TO_REOPEN_BELENDER.includes(substatus);
    }

    hideContinueOTPButton(): boolean {
        const request = this.freelancerRequestDetailService.request;
        if (!request) return true;

        const status = request.personQuery.status;
        if (STATES_TO_HIDE_CONTINUE_OTP_BUTTON.includes(status)) return true;

        const substatus = request.personQuery.substatus;
        return !SUBSTATES_TO_REOPEN_BELENDER.includes(substatus);
    }

    hideCancelOTPButton(): boolean {
        const request = this.freelancerRequestDetailService.request;
        if (!request) return true;

        const status = request.personQuery.status;
        return STATES_TO_HIDE_CANCEL_OTP_BUTTON.includes(status);
    }

    hideDownloadZip(): boolean {
        const documents = this.freelancerRequestDetailService.request.documents;
        return Object.keys(documents ?? {}).length === 0;
    }

    async downloadZIP(): Promise<void> {
        this.isDownloadZipButtonDisabled = true;
        let extraFiles: Record<string, string> | undefined = undefined;

        if (this.irpfTableService.isInit()){
            const excelBlob = await this.irpfTableService.generateExcelBlob();
            const excelBuffer = await excelBlob.arrayBuffer();
            const base64Excel = this.utilsService.arrayBufferToBase64(excelBuffer);
            extraFiles = {
                ['IRPF.xlsx']: base64Excel
            };
        }

        const req: FilesRequest = {
            detail: this.freelancerRequestDetailService.request,
            extraFiles: extraFiles,
            includeJSON: false
        };
        await this.utilsService.downloadZIP(req);
        this.isDownloadZipButtonDisabled = false;
    }
}
